import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import PageHeadline from '../components/PageHeadline';
import supabase from '../supabase/supabase';
import './InfluencerPortal.css';

const { FiEye, FiEyeOff } = FiIcons;

function getFilterRange(type, value) {
  if (type === 'date') {
    const start = new Date(`${value}T00:00:00`);
    const end = new Date(start);
    end.setDate(end.getDate() + 1);
    return { start, end };
  }
  if (type === 'year') {
    return {
      start: new Date(Number(value), 0, 1),
      end: new Date(Number(value) + 1, 0, 1)
    };
  }
  const [year, month] = value.split('-').map(Number);
  return {
    start: new Date(year, month - 1, 1),
    end: new Date(year, month, 1)
  };
}

function InfluencerPortal() {
  const { token } = useParams();
  const today = new Date().toISOString().slice(0, 10);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [details, setDetails] = useState(null);
  const [error, setError] = useState('');
  const [filterType, setFilterType] = useState('month');
  const [filterValue, setFilterValue] = useState(today.slice(0, 7));
  const [loading, setLoading] = useState(false);

  const loadFilteredResults = async (type, value) => {
    const { start, end } = getFilterRange(type, value);
    setLoading(true);
    const { data, error: reportError } = await supabase.rpc('get_influencer_report_1750000016', {
      token_input: token?.trim(),
      password_input: password.trim(),
      start_input: start.toISOString(),
      end_input: end.toISOString()
    });
    setLoading(false);
    if (reportError || !data?.length) {
      setError('Unable to load results for the selected period.');
      return;
    }
    setDetails(data[0]);
  };

  const unlock = async (event) => {
    event.preventDefault();
    setError('');
    const { data, error: portalError } = await supabase.rpc('get_influencer_portal_1750000011', {
      token_input: token?.trim(),
      password_input: password.trim()
    });
    if (portalError || !data?.length) {
      setError('The password is incorrect or this portal is unavailable.');
      return;
    }
    setDetails(data[0]);
    await loadFilteredResults(filterType, filterValue);
  };

  const changeFilterType = async (type) => {
    let value = today.slice(0, 7);
    if (type === 'date') value = today;
    if (type === 'year') value = today.slice(0, 4);
    setFilterType(type);
    setFilterValue(value);
    await loadFilteredResults(type, value);
  };

  const changeFilterValue = async (value) => {
    setFilterValue(value);
    await loadFilteredResults(filterType, value);
  };

  if (!details) {
    return (
      <main className="influencer-portal">
        <form className="influencer-login" onSubmit={unlock}>
          <span className="eyebrow">Darshana partner portal</span>
          <PageHeadline>Influencer campaign performance</PageHeadline>
          <p>Enter the password provided by the Darshana team to view your private promotion dashboard.</p>
          <div className="portal-password-field">
            <input
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              placeholder="Portal password"
              required
            />
            <button
              type="button"
              className="portal-password-toggle"
              onClick={() => setShowPassword((current) => !current)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              title={showPassword ? 'Hide password' : 'Show password'}
            >
              <SafeIcon icon={showPassword ? FiEyeOff : FiEye} />
            </button>
          </div>
          {error && <p className="portal-error">{error}</p>}
          <button className="button primary gradient-btn" type="submit">View my results</button>
        </form>
      </main>
    );
  }

  return (
    <main className="influencer-portal">
      <section className="influencer-results">
        <span className="eyebrow">Welcome, {details.name}</span>
        <PageHeadline>Influencer campaign performance</PageHeadline>
        <p className="portal-code">Your code: <strong>{details.promo_code}</strong> · {details.discount_percent}% customer discount</p>
        <div className="portal-filter-panel">
          <div className="portal-filter-buttons">
            <button className={filterType === 'date' ? 'active' : ''} onClick={() => changeFilterType('date')}>Date</button>
            <button className={filterType === 'month' ? 'active' : ''} onClick={() => changeFilterType('month')}>Month</button>
            <button className={filterType === 'year' ? 'active' : ''} onClick={() => changeFilterType('year')}>Year</button>
          </div>
          {filterType === 'date' && <input type="date" value={filterValue} onChange={(event) => changeFilterValue(event.target.value)} />}
          {filterType === 'month' && <input type="month" value={filterValue} onChange={(event) => changeFilterValue(event.target.value)} />}
          {filterType === 'year' && <input type="number" min="2020" max="2100" value={filterValue} onChange={(event) => changeFilterValue(event.target.value)} />}
        </div>
        {loading && <p className="portal-loading">Updating results…</p>}
        {error && <p className="portal-error">{error}</p>}
        <div className="portal-stats">
          <div><span>Promo uses</span><strong>{details.uses_count}</strong></div>
          <div><span>Sales credited</span><strong>Rs. {Number(details.sales_total).toLocaleString()}</strong></div>
          <div><span>Your 20% commission</span><strong>Rs. {Number(details.commission_total).toLocaleString()}</strong></div>
          <div><span>Payouts received</span><strong>Rs. {Number(details.payouts_total || 0).toLocaleString()}</strong></div>
        </div>
        <div className="portal-balance-summary">
          <span>Remaining balance for selected period</span>
          <strong>Rs. {Number(details.balance_total ?? details.commission_total).toLocaleString()}</strong>
          <p>Monthly and early payouts recorded by the admin are deducted from this balance.</p>
        </div>
      </section>
    </main>
  );
}

export default InfluencerPortal;